
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { createClient, getUserApiKey } from '@/lib/supabase-client'

export default function ImageUploader() {
  const [imageUrl, setImageUrl] = useState('')
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [mode, setMode] = useState<'short' | 'long' | 'seo'>('short')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result as string
        setUploadedImage(base64String)
        setImageUrl('')
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUrlSubmit = () => {
    if (imageUrl) {
      setUploadedImage(imageUrl)
      generateAltText(imageUrl)
    }
  }

  const handleUploadedImage = () => {
    if (uploadedImage) {
      // For uploaded images, we need to convert to a URL
      // In production, you'd upload to a storage service first
      generateAltText(uploadedImage)
    }
  }

  const generateAltText = async (imgUrl: string) => {
    setLoading(true)
    setError(null)
    setResult(null)

    try {
      // Use the public proxy endpoint that only requires user's API key
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://eutekssvzhtkwzperkzb.supabase.co/functions/v1'
      // Use /generate-public for Supabase direct, /generate for Vercel API
      const generateEndpoint = apiBaseUrl.includes('supabase.co') ? '/generate-public' : '/generate'
      const endpoint = `${apiBaseUrl}${generateEndpoint}`
      // Determine if we're using the public endpoint
      const usePublicEndpoint = endpoint.includes('-public')
      
      // Get API key from session/profile
      const supabase = createClient()
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        setError('Please sign in to generate alt text. Sign up for a free account to get started!')
        setLoading(false)
        return
      }

              const apiKey = await getUserApiKey(supabase, session.user.id)

              if (!apiKey) {
                setError('Unable to retrieve API key. Please try refreshing the page or contact support.')
                setLoading(false)
                return
              }

      // Real API call
      // Use custom header 'x-api-key' to avoid JWT validation issues
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
      
      if (!apiKey) {
        setError('API key not found. Please refresh the page or contact support.')
        setLoading(false)
        return
      }

      if (!supabaseAnonKey) {
        setError('Supabase configuration error. Please check your environment variables.')
        setLoading(false)
        return
      }

      // Build headers object
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      
      // For public endpoint, we only need the user's API key in the body
      // For internal endpoint, we need Supabase anon key
      // Since we're using public endpoints, we don't need these headers
      // The API key is sent in the request body
      
      
      // Check if imgUrl is a base64 data URL (starts with data:)
      const isBase64 = imgUrl.startsWith('data:')
      
      // For base64 images, use directly - OpenAI API supports base64 encoded images
      // OpenAI has a 20MB limit, but we'll use 10MB to be safe (base64 is ~33% larger than binary)
      let finalImageUrl = imgUrl
      if (isBase64) {
        // Check size limit (OpenAI has a 20MB limit, but we'll use 10MB to be safe)
        // Base64 encoding increases size by ~33%, so 10MB base64 ≈ 7.5MB binary
        if (imgUrl.length > 10000000) { // ~10MB limit
          setError('Image is too large. Please use an image URL instead or compress the image to under 7MB.')
          setLoading(false)
          return
        }
        // Use base64 directly - OpenAI Vision API supports base64 encoded images
        // The format is already correct: data:image/jpeg;base64,<base64_data>
        finalImageUrl = imgUrl
      }
      
              const requestBody = {
                image_url: finalImageUrl, // Use the processed URL (either original or uploaded to storage)
                mode: mode,
                api_key: apiKey, // Also send in body as fallback if headers don't work
                source: 'web', // Track that this is from the web dashboard
              }
              
              const response = await fetch(endpoint, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(requestBody),
              })

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to generate alt text' }))
                throw new Error(errorData.message || `API error: ${response.status}`)
              }

      const data = await response.json()
      const altText = data.alt_text || data.short_alt_text || data.long_alt_text || data.seo_alt_text
      
      if (!altText || altText.trim() === '') {
        throw new Error('Received empty response from API. Please try again.')
      }
      
      setResult(altText)
    } catch (err: any) {
      setError(err.message || 'Failed to generate alt text. Please try again or contact support if the issue persists.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="glass-card rounded-xl p-8">
      <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
        Generate AI Alt Text
      </h2>

      <div className="space-y-6">
        {/* Mode Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Alt Text Mode
          </label>
          <div className="flex space-x-4">
            <button
              onClick={() => setMode('short')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                mode === 'short'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              Short
            </button>
            <button
              onClick={() => setMode('long')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                mode === 'long'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              Long
            </button>
            <button
              onClick={() => setMode('seo')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                mode === 'seo'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              SEO
            </button>
          </div>
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Upload Image or Enter URL
          </label>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500 w-full"
                onKeyPress={(e) => e.key === 'Enter' && handleUrlSubmit()}
              />
              <button
                onClick={handleUrlSubmit}
                disabled={!imageUrl || loading}
                className="px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition w-full sm:w-auto"
              >
                Use URL
              </button>
            </div>
            <div className="relative">
              <div className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center hover:border-primary-500 dark:hover:border-primary-400 transition bg-gray-50 dark:bg-slate-900/50">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  <span className="font-medium text-primary-600 dark:text-primary-400">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">PNG, JPG, GIF up to 10MB</p>
              </div>
            </div>
          </div>
        </div>

        {/* Preview */}
        {(uploadedImage || imageUrl) && (
          <div className="space-y-4">
            <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700">
              <img
                src={uploadedImage || imageUrl}
                alt="Preview"
                className="w-full h-64 object-contain bg-gray-50 dark:bg-slate-900"
              />
            </div>
            <button
              onClick={handleUploadedImage}
              disabled={loading}
              className="w-full px-6 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition text-lg"
            >
              {loading ? 'Generating Alt Text...' : 'Generate Alt Text'}
            </button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Generated Alt Text:</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">{result}</p>
            <button
              onClick={() => {
                navigator.clipboard.writeText(result)
                alert('Copied to clipboard!')
              }}
              className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition"
            >
              Copy to Clipboard
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <p className="text-red-800 dark:text-red-300">{error}</p>
            {error.includes('sign up') && (
              <Link
                to="/auth/signup"
                className="mt-4 inline-block px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition"
              >
                Sign Up Free
              </Link>
            )}
          </div>
        )}

        {/* Info */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <strong>Note:</strong> You need to be signed in to generate alt text. Sign up for a free account to get started with 50 free credits (one-time).
          </p>
        </div>
      </div>
    </div>
  )
}

